"use client"

import { useId, useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Calculator } from "lucide-react"
import { EnergyCalculatorRecommendations } from './energy-calculator-recommendations'

interface InlineEnergyCalculatorProps {
  electricityRate: number // cents per kWh
  cityName: string
}

export function InlineEnergyCalculator({ electricityRate, cityName }: InlineEnergyCalculatorProps) {
  const kwhInputId = useId()
  const [kwhInput, setKwhInput] = useState<string>("500")
  const [showResult, setShowResult] = useState(false)

  const kwhValue = parseFloat(kwhInput) || 0
  const annualCost = (kwhValue * electricityRate / 100).toFixed(2)
  const monthlyCost = (parseFloat(annualCost) / 12).toFixed(2)

  const handleCalculate = () => {
    setShowResult(true)
  }

  return (
    <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center">
          <Calculator className="h-5 w-5 text-[#0469f0]" />
        </div>
        <span className="font-medium text-gray-900">Quick Energy Calculator</span>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <div className="flex-1">
          {/* FRIDGE-1505: the label names its input (it had neither htmlFor nor a nested input). */}
          <label htmlFor={kwhInputId} className="text-sm text-gray-600 mb-1 block">
            Annual kWh (from Energy Guide label)
          </label>
          <Input
            id={kwhInputId}
            name="annual-kwh"
            type="number"
            value={kwhInput}
            onChange={(e) => {
              setKwhInput(e.target.value)
              setShowResult(false)
            }}
            placeholder="e.g., 500"
            className="w-full"
          />
        </div>
        <Button variant="ctaPrimary" onClick={handleCalculate}>
          Calculate
        </Button>
      </div>

      {showResult && kwhValue > 0 && (
        <>
          <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <div className="text-center">
              <p className="text-sm text-gray-500">In {cityName} at {electricityRate.toFixed(1)}¢/kWh:</p>
              <p className="text-3xl font-bold text-[#0469f0] mt-1">{`$${annualCost}`}/year</p>
              <p className="text-sm text-gray-500">~{`$${monthlyCost}`}/month</p>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Use your appliance’s EnergyGuide annual kWh figure or measured consumption. This estimate uses the displayed area-average rate; your tariff and usage may differ.
            </p>
          </div>
          
          <EnergyCalculatorRecommendations 
            yearlyCost={parseFloat(annualCost)}
            kwhUsage={kwhInput}
          />
        </>
      )}
    </div>
  )
}
